/**
 * Environment-based Component Factory
 * Conditionally loads development features only in development builds
 * Production builds never import or bundle development code
 */

/**
 * Get the appropriate test user provider based on environment
 * In production: Returns a no-op passthrough component
 * In development: Returns the TestUserProvider with full functionality
 */
export const getTestUserProvider = () => {
  if (process.env.NODE_ENV === 'development') {
    // Dynamic import - only loaded in development
    const { TestUserProvider } = require('../development/TestUserProvider');
    return TestUserProvider;
  }
  
  // Production: No-op passthrough component
  return ({ children }) => children;
};

/**
 * Get the test user hook based on environment
 * In production: Returns a mock hook that always returns production data
 * In development: Returns the actual test user hook
 */
export const getTestUserHook = () => {
  if (process.env.NODE_ENV === 'development') {
    // Dynamic import - only loaded in development
    const { useTestUser } = require('../development/TestUserProvider');
    return useTestUser;
  }
  
  // Production: Mock hook that returns no test functionality
  return () => ({
    isTestMode: false,
    activeTestUser: null,
    testOrganization: null,
    getEffectiveUser: (user) => user,
    getEffectiveOrganization: (user) => user?.company
  });
};

/**
 * Get enhanced components with test user support
 * In production: Returns original components unchanged
 * In development: Returns wrapped components with test user context
 */
export const getEnhancedComponents = (components) => {
  if (process.env.NODE_ENV === 'development') {
    // Dynamic import - only loaded in development
    const { 
      withEffectiveUser, 
      withEffectiveOrganization, 
      withEffectiveUserAndOrg 
    } = require('../development/UserContextWrapper');
    
    return {
      // Components that need user context only
      GreetingComponent: withEffectiveUser(components.GreetingComponent),
      GreetingComponentV2: withEffectiveUser(components.GreetingComponentV2),
      GreetingComponentV3: withEffectiveUser(components.GreetingComponentV3),
      Header: withEffectiveUserAndOrg(components.Header),
      
      // Components that need organization context
      ChatInput: withEffectiveOrganization(components.ChatInput),
      MobileChatInput: withEffectiveOrganization(components.MobileChatInput),
      
      // Other components pass through unchanged
      Chat: components.Chat,
      MobileHeader: components.MobileHeader
    };
  }
  
  // Production: Return original components unchanged
  return components;
}; 